import { ColumnType } from 'antd/es/table';
import { useDispatch } from "react-redux";
import React, { useEffect, useState, useMemo } from "react";
import { updateMenuData } from "../../store/slices/misc/menuData";
import Template1 from "./ReportTemplates/Template1";
import { useLocation, useNavigate } from "react-router-dom";
import reportApis from "../../api/reports";
import { TooltipComponent } from "../../components/Component";
import dayjs from 'dayjs';
import { Drawer, Table, Progress, Tooltip } from 'antd';
import { tasksApis } from '../../api/NewTasks';

interface TaskItem {
    taskId: string;
    taskName: string;
    taskOwnerName: string;
    taskPlannedStartDate: string;
    taskPlannedEndDate: string;
    taskActualStartDate: string;
    taskActualEndDate: string;
    priorityName: string;
    completionPercentage: number;
    taskStatus: string;
    uid: string;
    overdueStatus: string;
}

const StatusWiseTaskPerformanceReport = () => {
    const [userIdFilterData, setUserIdFilterData] = useState([]);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [overdueDrawerVisible, setOverdueDrawerVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState<{ userId: string, userName: string } | null>(null);
    const [tasksData, setTasksData] = useState<TaskItem[]>([]);
    const [overdueTasksData, setOverdueTasksData] = useState<TaskItem[]>([]);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { id: projectId, projectName } = location.state || {};

    const taskColumns: ColumnType<TaskItem>[] = useMemo(() => [
        {
            title: "Task Name",
            dataIndex: "taskName",
            key: "taskName",
            width: 250,
            fixed: "left",
            render(text: any, record: any) {
                return (
                    <>
                        <span
                            className="inlineTableCellLink"
                            id={`task-${record?.taskId}`}
                            onClick={() => {
                                navigate(`/pms/project/task-detail/${record.taskId}`, {
                                    state: {
                                        projectId,
                                        projectName,
                                        returnPath: location.pathname,
                                        returnState: location.state,
                                    },
                                });
                            }}
                        >
                            {text}
                        </span>
                    </>
                );
            },
        },
        {
            title: "Owner",
            dataIndex: "taskOwnerName",
            key: "taskOwnerName",
            width: 250,
        },
        {
            title: "Start Date",
            dataIndex: "taskPlannedStartDate",
            key: "taskPlannedStartDate",
            width: 200,
            render: (text: string) => text || "N/A"
        },
        {
            title: "End Date",
            dataIndex: "taskPlannedEndDate",
            key: "taskPlannedEndDate",
            width: 200,
            render: (text: string) => text || "N/A",
        },
        {
            title: "Priority",
            dataIndex: "priorityName",
            key: "priorityName",
            width: 150,
        },
        {
            title: "Progress",
            dataIndex: "completionPercentage",
            key: "completionPercentage",
            width: 200,
            render: (data: any) => {
                return (
                    <Tooltip title={`${data}% Complete`} placement="top">
                        <Progress
                            strokeLinecap="butt"
                            size={[186, 10]}
                            percent={data}
                            status="active"
                            strokeColor={{
                                from: "#87d068",
                                to: "#87d068",
                            }}
                        />
                    </Tooltip>
                );
            },
        },
        {
            title: "Status",
            dataIndex: "taskStatus",
            key: "taskStatus",
            width: 150,
            fixed: "right",
        },
    ], [dispatch]);

    const overdueTaskColumns: ColumnType<TaskItem>[] = useMemo(() => [
        {
            title: "Task Name",
            dataIndex: "taskName",
            key: "taskName",
            width: 200,
            fixed: "left",
            render(text: any, record: any) {
                return (
                    <>
                        <span
                            className="inlineTableCellLink"
                            id={`task-${record?.taskId}`}
                            onClick={() => {
                                navigate(`/pms/project/task-detail/${record.taskId}`, {
                                    state: {
                                        projectId,
                                        projectName,
                                        returnPath: location.pathname,
                                        returnState: location.state,
                                    },
                                });
                            }}
                        >
                            {text}
                        </span>
                    </>
                );
            },
        },
        {
            title: "Planned Start Date",
            dataIndex: "plannedStartDate",
            key: "plannedStartDate",
            width: 150,
            render: (text: string) => text || "N/A",
        },
        {
            title: "Planned End Date",
            dataIndex: "plannedEndDate",
            key: "plannedEndDate",
            width: 150,
            render: (text: string) => text || "N/A",
        },
        {
            title: "Actual Start Date",
            dataIndex: "actualStartDate",
            key: "actualStartDate",
            width: 150,
            render: (text: string) => text || "N/A",
        },
        {
            title: "Actual End Date",
            dataIndex: "taskActualEndDate",
            key: "taskActualEndDate",
            width: 150,
            render: (text: string) => text || "N/A",
        },
        {
            title: "Owner",
            dataIndex: "userName",
            key: "userName",
            width: 150,
        },
        {
            title: "Delayed Status",
            dataIndex: "delayedStatus",
            key: "delayedStatus",
            width: 150,
            render: (text: string) => text || "N/A",
        },
        {
            title: "Task Status",
            dataIndex: "taskStatus",
            key: "taskStatus",
            width: 150,
        },
        {
            title: "Completion %",
            dataIndex: "completedPercentage",
            key: "completedPercentage",
            width: 120,
            render: (text: number) => `${text}%` || "N/A",
        },
    ], [dispatch]);

    const handleTotalTasksClick = async (record: any) => {
        setSelectedUser({
            userId: record.userId,
            userName: record.assigedTo
        });
        setLoading(true);
        setDrawerVisible(true);

        try {
            const statusToSend = record.taskStatus?.toLowerCase() === 'active' ? 'Open' : record.taskStatus;

            const response = await tasksApis.listTasks({
                page: 1,
                pageSize: 0,
                projectIds: projectId,
                pageDiff: 0,
                sortOrder: "ASC",
                direction: "P",
                taskStatus: statusToSend,
                taskOwnerIds: record.assigedId,
            });
            const { status, data } = response || {};
            if (status) {
                setTasksData(data?.list || []);
            }
        } catch (error) {
            console.error("Error fetching tasks:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelayTasksClick = async (record: any) => {
        setSelectedUser({
            userId: record.assigedId,
            userName: record.assigedTo
        });
        setLoading(true);
        setOverdueDrawerVisible(true);

        try {
            const response = await reportApis.fetchPMSReports({
                page: 1,
                pageSize: 10,
                projectIds: projectId,
                pageDiff: 0,
                sortOrder: "DESC",
                direction: "P",
                reportName: "delayedDaysStatusWiseTaskPerformance",
                // taskStatus: "Overdue", /// pass delayed
                taskOwnerIds: record.assigedId,
            });

            const { status, data } = response || {};
            if (status) {
                setOverdueTasksData(data?.list || []);
            }
        } catch (error) {
            console.error("Error fetching overdue tasks:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserDropdown = async () => {
        try {
            const response = await tasksApis.getOwnersFilterData(
                projectId?.toString()
            );
            const { data, status } = response || {};
            if (status) {
                const options = data.map((item: any) => {
                    return {
                        label: item.userName,
                        value: item.userId,
                    };
                });
                setUserIdFilterData(options);
            }
        } catch (error) {
            console.error("error")
        }
    }

    useEffect(() => {
        fetchUserDropdown();
        dispatch(
            updateMenuData([
                {
                    text: "Status-Wise Task Performance Report",
                    link: "pms/statuswise-task-performance-report",
                },
            ])
        );
    }, []);

    const columns: ColumnType<any>[] = [
        {
            title: "Status",
            dataIndex: "taskStatus",
            key: "taskStatus",
            width: 50,
            render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
        },
        {
            title: "Total Tasks",
            dataIndex: "totalTasks",
            key: "totalTasks",
            width: 50,
            render(text: any, record: any) {
                return (
                    <>
                        <div
                            className="inlineTableCellLink"
                            id={`task-${record?.taskId}`}
                            onClick={() => handleTotalTasksClick(record)}
                        >
                            {text}
                        </div>
                        <TooltipComponent
                            iconClass="info-circle text-info"
                            icon={""}
                            id={`task-${record?.taskId}`}
                            direction="right"
                            text={"Click To View " + text + " Details"}
                            containerClassName={"mt-1"}
                        />
                    </>
                );
            },
        },
        {
            title: "Delayed Tasks",
            dataIndex: "DelayedTasks",
            key: "DelayedTasks",
            width: 50,
            render(text: any, record: any) {
                return (
                    <>
                        <div
                            className="inlineTableCellLink"
                            id={`phase-${record?.phaseId}`}
                            onClick={() => handleDelayTasksClick(record)}
                        >
                            {text}
                        </div>
                        <TooltipComponent
                            iconClass="info-circle text-info"
                            icon={""}
                            id={`phase-${record?.phaseId}`}
                            direction="right"
                            text={"Click To View " + text + " Details"}
                            containerClassName={"mt-1"}
                        />
                    </>
                );
            },
        },
        {
            title: "Owner With Most Tasks",
            dataIndex: "assigedTo",
            key: "assigedTo",
            width: 100,
            render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
        },
    ];

    return (
        <>
            <Template1
                moduleKey={window.location.pathname}
                columns={columns}
                pageTitle='Status-wise Task Performance Report'
                pageDescription=''
                previousPath={{
                    link: "/pms/project/reports",
                    state: location.state,
                }}
                filters={{
                    customFilters: [
                        {
                            type: "multiple_selection",
                            name: "UserId",
                            label: "Users",
                            options: userIdFilterData,
                        },
                    ]
                }}
            />

            <Drawer
                title={`Tasks for ${selectedUser?.userName || 'User'}`}
                width={1200}
                onClose={() => setDrawerVisible(false)}
                open={drawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
            >
                <Table
                    columns={taskColumns}
                    dataSource={tasksData}
                    loading={loading}
                    scroll={{ x: 1300 }}
                    rowKey="taskId"
                />
            </Drawer>

            <Drawer
                title={`Delayed Tasks for ${selectedUser?.userName || 'User'}`}
                width={1200}
                onClose={() => setOverdueDrawerVisible(false)}
                open={overdueDrawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
            >
                <Table
                    columns={overdueTaskColumns}
                    dataSource={overdueTasksData}
                    loading={loading}
                    scroll={{ x: 1500 }}
                    rowKey="taskId"
                />
            </Drawer>
        </>
    );
}

export default StatusWiseTaskPerformanceReport;